/**
 * FERP PDF Annotation Layer (DRR-079)
 *
 * React overlay positioned on PDF.js canvas. Supports:
 * - Highlight: rectangular selection with color
 * - Text comment: positioned note
 * - Stamp: APPROVED/REJECTED/REVIEWED
 *
 * Annotations persist as JSON array via Document primitive's dynamodb_content
 * field through existing updateDocument GraphQL mutation.
 *
 * FERP Constraints:
 * - Backend Snowflake IDs only — temporary IDs prefixed with "pending_" until saved
 * - Mantine v8.x ONLY
 * - useAuth() for createdBy
 */

import { useState, useCallback, useRef, type MouseEvent } from 'react';
import {
  Box,
  Text,
  ActionIcon,
  Tooltip,
  Paper,
} from '@mantine/core';
import { IconX } from '@tabler/icons-react';
import { useAuth } from '@/auth';
import type { PdfAnnotation, AnnotationMode, StampValue, PdfRect } from './types';

interface AnnotationLayerProps {
  /** Annotations for current page only */
  annotations: PdfAnnotation[];
  /** Current page number (1-indexed) */
  page: number;
  /** Current zoom scale */
  scale: number;
  /** Active annotation mode */
  mode: AnnotationMode;
  /** Selected stamp value */
  selectedStamp: StampValue;
  /** Callback to add a new annotation */
  onAddAnnotation?: (annotation: PdfAnnotation) => void;
  /** Callback to remove an annotation */
  onRemoveAnnotation?: (annotationId: string) => void;
}

const HIGHLIGHT_COLORS: Record<string, string> = {
  yellow: 'rgba(255, 235, 59, 0.35)',
  green: 'rgba(76, 175, 80, 0.35)',
  blue: 'rgba(33, 150, 243, 0.35)',
  red: 'rgba(244, 67, 54, 0.35)',
};

const STAMP_COLORS: Record<StampValue, string> = {
  APPROVED: 'var(--mantine-color-green-6)',
  REJECTED: 'var(--mantine-color-red-6)',
  REVIEWED: 'var(--mantine-color-blue-6)',
};

export function AnnotationLayer({
  annotations,
  page,
  scale,
  mode,
  selectedStamp,
  onAddAnnotation,
  onRemoveAnnotation,
}: AnnotationLayerProps) {
  const overlayRef = useRef<HTMLDivElement>(null);
  const [dragStart, setDragStart] = useState<{ x: number; y: number } | null>(null);
  const [dragCurrent, setDragCurrent] = useState<{ x: number; y: number } | null>(null);
  const [highlightColor] = useState('yellow');
  const { user } = useAuth();

  const getRelativePosition = useCallback(
    (e: MouseEvent): { x: number; y: number } | null => {
      const overlay = overlayRef.current;
      if (!overlay) return null;
      const rect = overlay.getBoundingClientRect();
      return {
        x: (e.clientX - rect.left) / rect.width,
        y: (e.clientY - rect.top) / rect.height,
      };
    },
    [],
  );

  const handleMouseDown = useCallback(
    (e: MouseEvent) => {
      if (!mode || !onAddAnnotation) return;
      if (mode === 'stamp') {
        // Stamps are placed on click
        const pos = getRelativePosition(e);
        if (!pos) return;
        onAddAnnotation({
          id: `pending_${Date.now()}`,
          type: 'stamp',
          page,
          rect: { x: pos.x - 0.05, y: pos.y - 0.02, width: 0.1, height: 0.04 },
          content: selectedStamp,
          createdBy: user?.id ?? 'unknown',
          createdAt: new Date().toISOString(),
        });
        return;
      }
      if (mode === 'text') {
        const pos = getRelativePosition(e);
        if (!pos) return;
        const content = window.prompt('Enter comment:');
        if (!content) return;
        onAddAnnotation({
          id: `pending_${Date.now()}`,
          type: 'text',
          page,
          rect: { x: pos.x, y: pos.y, width: 0.15, height: 0.05 },
          content,
          createdBy: user?.id ?? 'unknown',
          createdAt: new Date().toISOString(),
        });
        return;
      }
      // Highlight: start drag
      const pos = getRelativePosition(e);
      if (pos) {
        setDragStart(pos);
        setDragCurrent(pos);
      }
    },
    [mode, page, selectedStamp, onAddAnnotation, getRelativePosition, user?.id],
  );

  const handleMouseMove = useCallback(
    (e: MouseEvent) => {
      if (!dragStart || mode !== 'highlight') return;
      const pos = getRelativePosition(e);
      if (pos) setDragCurrent(pos);
    },
    [dragStart, mode, getRelativePosition],
  );

  const handleMouseUp = useCallback(() => {
    if (!dragStart || !dragCurrent || mode !== 'highlight' || !onAddAnnotation) {
      setDragStart(null);
      setDragCurrent(null);
      return;
    }
    const rect: PdfRect = {
      x: Math.min(dragStart.x, dragCurrent.x),
      y: Math.min(dragStart.y, dragCurrent.y),
      width: Math.abs(dragCurrent.x - dragStart.x),
      height: Math.abs(dragCurrent.y - dragStart.y),
    };
    // Minimum size check
    if (rect.width > 0.01 && rect.height > 0.01) {
      onAddAnnotation({
        id: `pending_${Date.now()}`,
        type: 'highlight',
        page,
        rect,
        content: '',
        color: highlightColor,
        createdBy: user?.id ?? 'unknown',
        createdAt: new Date().toISOString(),
      });
    }
    setDragStart(null);
    setDragCurrent(null);
  }, [dragStart, dragCurrent, mode, page, highlightColor, onAddAnnotation, user?.id]);

  const cursorStyle =
    mode === 'highlight'
      ? 'crosshair'
      : mode === 'text'
        ? 'text'
        : mode === 'stamp'
          ? 'copy'
          : 'default';

  return (
    <Box
      ref={overlayRef}
      onMouseDown={handleMouseDown}
      onMouseMove={handleMouseMove}
      onMouseUp={handleMouseUp}
      style={{
        position: 'absolute',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        cursor: cursorStyle,
        pointerEvents: mode ? 'auto' : 'none',
        zIndex: 10,
      }}
    >
      {/* Render existing annotations */}
      {annotations.map((ann) => (
        <AnnotationOverlay
          key={ann.id}
          annotation={ann}
          scale={scale}
          onRemove={onRemoveAnnotation}
        />
      ))}

      {/* Render drag preview for highlight */}
      {dragStart && dragCurrent && mode === 'highlight' && (
        <Box
          style={{
            position: 'absolute',
            left: `${Math.min(dragStart.x, dragCurrent.x) * 100}%`,
            top: `${Math.min(dragStart.y, dragCurrent.y) * 100}%`,
            width: `${Math.abs(dragCurrent.x - dragStart.x) * 100}%`,
            height: `${Math.abs(dragCurrent.y - dragStart.y) * 100}%`,
            backgroundColor: HIGHLIGHT_COLORS[highlightColor],
            border: '1px dashed rgba(0,0,0,0.3)',
            pointerEvents: 'none',
          }}
        />
      )}
    </Box>
  );
}

/** Individual annotation overlay element */
function AnnotationOverlay({
  annotation,
  scale: _scale,
  onRemove,
}: {
  annotation: PdfAnnotation;
  scale: number;
  onRemove?: (id: string) => void;
}) {
  const [hovered, setHovered] = useState(false);
  const { rect, type, content, color } = annotation;

  const baseStyle: React.CSSProperties = {
    position: 'absolute',
    left: `${rect.x * 100}%`,
    top: `${rect.y * 100}%`,
    width: `${rect.width * 100}%`,
    height: `${rect.height * 100}%`,
    pointerEvents: 'auto',
  };

  if (type === 'highlight') {
    return (
      <Tooltip
        label={`Highlight by ${annotation.createdBy}`}
        disabled={!hovered}
      >
        <Box
          onMouseEnter={() => setHovered(true)}
          onMouseLeave={() => setHovered(false)}
          style={{
            ...baseStyle,
            backgroundColor: HIGHLIGHT_COLORS[color ?? 'yellow'],
            borderRadius: 2,
          }}
        >
          {hovered && onRemove && (
            <ActionIcon
              size="xs"
              variant="filled"
              color="red"
              style={{ position: 'absolute', top: -8, right: -8 }}
              onClick={(e) => {
                e.stopPropagation();
                onRemove(annotation.id);
              }}
            >
              <IconX size={10} />
            </ActionIcon>
          )}
        </Box>
      </Tooltip>
    );
  }

  if (type === 'text') {
    return (
      <Paper
        onMouseEnter={() => setHovered(true)}
        onMouseLeave={() => setHovered(false)}
        shadow="sm"
        p={4}
        style={{
          ...baseStyle,
          backgroundColor: 'rgba(255, 243, 224, 0.95)',
          border: '1px solid var(--mantine-color-orange-3)',
          overflow: 'hidden',
          height: 'auto',
          minHeight: `${rect.height * 100}%`,
        }}
      >
        <Text size="xs" lineClamp={3}>
          {content}
        </Text>
        <Text size="xs" c="dimmed" mt={2}>
          {annotation.createdBy}
        </Text>
        {hovered && onRemove && (
          <ActionIcon
            size="xs"
            variant="filled"
            color="red"
            style={{ position: 'absolute', top: -8, right: -8 }}
            onClick={(e) => {
              e.stopPropagation();
              onRemove(annotation.id);
            }}
          >
            <IconX size={10} />
          </ActionIcon>
        )}
      </Paper>
    );
  }

  if (type === 'stamp') {
    const stampColor = STAMP_COLORS[content as StampValue] ?? 'var(--mantine-color-gray-6)';
    return (
      <Box
        onMouseEnter={() => setHovered(true)}
        onMouseLeave={() => setHovered(false)}
        style={{
          ...baseStyle,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          height: 'auto',
        }}
      >
        <Text
          fw={700}
          size="sm"
          style={{
            color: stampColor,
            border: `2px solid ${stampColor}`,
            borderRadius: 4,
            padding: '2px 8px',
            textTransform: 'uppercase',
            letterSpacing: 2,
            backgroundColor: 'rgba(255,255,255,0.9)',
            whiteSpace: 'nowrap',
          }}
        >
          {content}
        </Text>
        {hovered && onRemove && (
          <ActionIcon
            size="xs"
            variant="filled"
            color="red"
            style={{ position: 'absolute', top: -8, right: -8 }}
            onClick={(e) => {
              e.stopPropagation();
              onRemove(annotation.id);
            }}
          >
            <IconX size={10} />
          </ActionIcon>
        )}
      </Box>
    );
  }

  return null;
}
